<template>
    <div>
        <v-container fluid>

            <!-- FILTERS -->
            <v-row class="pt-3 pb-5">
                <v-col cols="12" md="9">
                    <filter-selector
                            @click="filterClicked"
                            @change="filtersChanged"
                            :filters="panelFilters"
                    ></filter-selector>
                </v-col>

                <!-- REPORTING MODE -->
                <v-col cols="12" md="3" class="text-right">
                    <v-btn-toggle v-model="mode" dense mandatory rounded style="background-color: #555" class="mt-0">
                        <v-btn text value="average">
                            Average
                        </v-btn>
                        <v-btn text value="sum">
                            Total
                        </v-btn>
                    </v-btn-toggle>

                    <!-- RELOAD BUTTON -->
                    <v-tooltip color="primary" left v-if="hasPermission('view data variables')">
                        <template v-slot:activator="{ on }">
                            <v-btn @click="reloadClicked()" v-on="on" icon>
                                <v-icon>mdi-refresh</v-icon>
                            </v-btn>
                        </template>
                        <span>Force reloading data</span>
                    </v-tooltip>
                </v-col>
            </v-row>

            <!-- STAT BOXES -->
            <v-row>
                <v-col cols="12" sm="6" md="4" xl="3">
                    <dashboard-value
                            :loading="loading"
                            title="Recruitment cost"
                            :sub-title="subTitleRecruitmentCost"
                            :prefixIcon="accountingCurrencyPrefixIcon"
                            :postfixIcon="accountingCurrencyPostfixIcon"
                            :value="getRecruitmentCost"
                            :auto-postfix="true"
                    ></dashboard-value>
                </v-col>

                <v-col cols="12" sm="6" md="4" xl="3" v-show="mode==='average'">

                    <dashboard-value
                            :loading="loading"
                            title="COST MULTIPLIER"
                            sub-title="# of recruits needed per one active panelist"
                            :value="getActiveMultiplier"
                            prefixIcon="mdi-close"
                            :auto-postfix="true"
                            color="red lighten-2"
                    ></dashboard-value>
                </v-col>

                <v-col cols="12" sm="6" md="4" xl="3" v-show="mode==='average'">
                    <dashboard-value

                            :loading="loading"
                            title="Actual recruitment cost"
                            :sub-title="subTitleRecruitmentActiveCost"
                            :value="getRecruitmentCostActive"
                            :prefixIcon="accountingCurrencyPrefixIcon"
                            :postfixIcon="accountingCurrencyPostfixIcon"
                            color="green darken-2"
                            :auto-postfix="true"
                    ></dashboard-value>
                </v-col>

                <v-col cols="12" sm="6" md="4" xl="3">
                    <dashboard-value
                            :loading="loading"
                            title="Revenue"
                            :sub-title="subTitleRevenue"
                            :value="getRevenue"
                            :prefixIcon="accountingCurrencyPrefixIcon"
                            :postfixIcon="accountingCurrencyPostfixIcon"
                            :auto-postfix="true"
                    ></dashboard-value>
                </v-col>

                <v-col cols="12" sm="6" md="4" xl="3">
                    <dashboard-value
                            :loading="loading"
                            title="Rewards awarded"
                            :sub-title="subTitleRewardAwarded"
                            :value="getRewardPointsAwarded"
                            :prefixIcon="accountingCurrencyPrefixIcon"
                            :postfixIcon="accountingCurrencyPostfixIcon"
                            :auto-postfix="true"
                    ></dashboard-value>
                </v-col>

                <v-col cols="12" sm="6" md="4" xl="3">
                    <dashboard-value
                            :loading="loading"
                            title="Rewards redeemed"
                            :sub-title="subTitleRewardRedeemed"
                            :value="getRewardPointsRedeemed"
                            :prefixIcon="accountingCurrencyPrefixIcon"
                            :postfixIcon="accountingCurrencyPostfixIcon"
                            :auto-postfix="true"
                    ></dashboard-value>
                </v-col>

                <v-col cols="12" sm="6" md="4" xl="3">
                    <dashboard-value
                            :loading="loading"
                            title="Rewards liability"
                            :sub-title="subTitleRewardLiability"
                            :value="getRewardPointsLiability"
                            :prefixIcon="accountingCurrencyPrefixIcon"
                            :postfixIcon="accountingCurrencyPostfixIcon"
                            color="red lighten-2"
                            :auto-postfix="true"
                    ></dashboard-value>
                </v-col>
            </v-row>

            <!-- DEBUG -->
            <v-row v-if="debug">
                <v-col cols="12">
                    <json :data="[]"></json>
                </v-col>
            </v-row>

        </v-container>

        <footer-note v-show="this.panelStats.createdAt">
            Report was created {{this.toHumanFrom(this.panelStats.createdAt)}}. {{hintText}}
        </footer-note>

        <footer-note v-show="!this.panelStats.createdAt">
            Report generating...
        </footer-note>
    </div>

</template>

<script>

    import DashboardValue from '../../../components/UI/DashboardValue';
    import {mapActions, mapMutations, mapState} from 'vuex';
    import FilterSelector from '../../../components/UI/Filter';
    import Json from 'vue-json-pretty';
    import {timeDisplay} from '../../../components/mixins/timeDisplayMixin';
    import {currencyMixin} from '../../../components/mixins/currencyMixin';
    import FooterNote from "../../../components/UI/FooterNote";

    const vuexModule = 'Financials';

    export default {

        mixins: [timeDisplay, currencyMixin],

        components: {
            DashboardValue,
            FilterSelector,
            Json,
            FooterNote
        },

        data() {
            return {
                debug: false,
            }
        },

        watch: {
            $route(to, from) {
                if (to.name === 'financial') {
                    this.loadData(false);
                }
            }
        },

        computed: {

            ...mapState('PanelSettings', [
                'settings',
            ]),

            ...mapState(vuexModule, [
                'loading',
                'panelFilters',
                'panelReportingMode',
                'panelStats',
            ]),

            hintText() {
                if (this.accountingCurrencyDefined) {
                    return 'Reported in ' + this.accountingCurrency + '.';
                }
                return 'Reported in Reward Points.';
            },

            mode: {
                get() {
                    return this.panelReportingMode;
                },
                set(value) {
                    this.setPanelReportingMode(value)
                }
            },

            modeName() {
                return this.mode === 'sum' ? 'Total' : 'Average';
            },

            subTitleRecruitmentCost() {
                return this.modeName + ' recruitment cost';
            },

            subTitleRecruitmentActiveCost() {
                return 'Actual recruitment cost per active panelist';
            },

            subTitleRevenue() {
                return this.modeName + ' revenue';
            },

            subTitleRewardAwarded() {
                return this.modeName + ' reward points awarded';
            },

            subTitleRewardRedeemed() {
                return this.modeName + ' reward points redeemed';
            },

            subTitleRewardLiability() {
                return this.modeName + ' reward points liability';
            },

            getRecruitmentCost() {
                return this.getAmount('recruitmentCost');
            },

            getRecruitmentCostActive() {
                return this.getAmount('recruitmentCostActive');
            },

            getRevenue() {
                return this.getAmount('revenue');
            },

            getRewardPointsAwarded() {
                return this.getAmount('rewards');
            },

            getRewardPointsRedeemed() {
                return this.getAmount('rewardsRedeemed');
            },

            getRewardPointsLiability() {
                return this.getAmount('rewardsLiability');
            },

            getActiveMultiplier() {
                return this.panelStats.activeMultiplier;
            }
        },

        methods: {

            getAmount(name) {
                return this.convertToAccountingCurrency(this.panelStats[name][this.mode]);
            },

            ...mapMutations(vuexModule, [
                'setPanelReportingMode',
                'setPanelFilters'
            ]),

            ...mapActions(vuexModule, [
                'getPanelStats'
            ]),

            reloadClicked() {
                this.loadData(true);
            },

            loadData(forceReload) {

                this.getPanelStats(forceReload)
                    .then(() => {

                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {message: 'Oops! Financial data failed to load!', type: 'error'});
                    });
            },

            filtersChanged(filters) {
                this.setPanelFilters(filters);
                this.loadData();
            },

            filterClicked(filter) {
                // No plain filters in use
            },
        },

        mounted() {
            this.loadData();
        },
    }
</script>
